"use client";

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { isAuthenticated, signOut, getAuthToken, getCurrentUserInfo, type CurrentUserInfo } from '@/lib/auth';
import { configureAmplify } from '@/lib/amplify-config';

interface AuthContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  getToken: () => Promise<string | null>;
  /** 表示用のログインユーザー情報 (未認証時は null) */
  user: CurrentUserInfo | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CurrentUserInfo | null>(null);
  const pathname = usePathname();

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const authenticated = await isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        // 表示用ユーザー情報を取得 (失敗してもログイン状態は維持。getCurrentUserInfo は throw しない)
        setUser(await getCurrentUserInfo());
      } else {
        setUser(null);
      }
      return authenticated;
    } catch (error) {
      console.error('認証確認エラー:', error);
      setIsLoggedIn(false);
      setUser(null);
      return false;
    }
  }, []);

  const logout = async () => {
    await signOut();
    setIsLoggedIn(false);
    setUser(null);
  };

  const getToken = async (): Promise<string | null> => {
    return await getAuthToken();
  };

  useEffect(() => {
    // 認証状態を確認
    const initAuth = async () => {
      setIsLoading(true);
      // Amplify設定を初期化（Cognitoモードの場合のみ必要）。
      // 設定完了前に fetchAuthSession が走ると「未設定」で未認証扱いになるため、必ず await する。
      if (process.env.NEXT_PUBLIC_AUTH_MODE !== 'local') {
        await configureAmplify();
      }
      await checkAuth();
      setIsLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  // パスが変更されたときに認証状態を再確認（ログインページを除く）
  useEffect(() => {
    if (!isLoading && !pathname.includes('/login')) {
      checkAuth();
    }
  }, [pathname, isLoading, checkAuth]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, isLoading, logout, checkAuth, getToken, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
